import { generateColors } from "@mantine/colors-generator";
import {
  Accordion,
  AppShellMain,
  Button,
  Checkbox,
  ColorInput,
  Container,
  createTheme,
  Dialog,
  Fieldset,
  Loader,
  LoadingOverlay,
  MantineSize,
  Modal,
  Notification,
  NumberInput,
  PasswordInput,
  PinInput,
  Portal,
  Radio,
  ScrollArea,
  SegmentedControl,
  Select,
  Switch,
  TextInput,
  Title,
  Tooltip
} from "@mantine/core";
import { DateInput, DatePickerInput } from "@mantine/dates";

import { cn } from "@/utils";

import classes from "./css/RadioCard.module.css";
import inputClasses from "./css/Theme.module.css";
import { DATE_PATTERNS } from "./date";
import { CustomLoader } from "./Loaders";

const DEFAULT_SIZE: MantineSize = "md";
const DEFAULT_VARIANT = "filled";
const DEFAULT_ERROR_CLASS_NAMES = "max-w-[250px]";

export const fontSizes = {
  "6xl": "calc(3.75rem * var(--mantine-scale))", // 60px
  "5xl": "calc(2.5rem * var(--mantine-scale))", // 40px
  "4xl": "calc(2rem * var(--mantine-scale))", // 32px
  "3xl": "calc(1.75rem * var(--mantine-scale))", // 28px
  "2xl": "calc(1.5rem * var(--mantine-scale))", // 24px
  xl: "calc(1.25rem * var(--mantine-scale))", // 20px
  lg: "calc(1.125rem * var(--mantine-scale))", // 18px
  md: "calc(1rem * var(--mantine-scale))", // 14px
  sm: "calc(0.875rem * var(--mantine-scale))", // 12px
  xs: "calc(0.75rem * var(--mantine-scale))", // 12px
  "2xs": "calc(0.625rem * var(--mantine-scale))", // 10px
  "3xs": "calc(0.5rem * var(--mantine-scale))", // 8px
  "4xs": "calc(0.375rem * var(--mantine-scale))" // 6px
} as const;

export const MANTINE_THEME = createTheme({
  primaryColor: "primary",
  defaultRadius: "md",
  cursorType: "pointer",
  fontSizes,
  colors: {
    primary: generateColors("#fc8f00")
  },
  components: {
    DatePickerInput: DatePickerInput.extend({
      defaultProps: {
        clearable: true,
        firstDayOfWeek: 1,
        valueFormat: DATE_PATTERNS.BR.DATE,
        modalProps: { transitionProps: { transition: "pop", duration: 300 } },
        highlightToday: true,
        locale: "pt-BR",
        defaultLevel: "decade",
        variant: DEFAULT_VARIANT,
        size: DEFAULT_SIZE
      },
      classNames: {
        label: inputClasses.label,
        error: DEFAULT_ERROR_CLASS_NAMES
      }
    }),
    Switch: Switch.extend({
      defaultProps: {
        withThumbIndicator: false
      }
    }),
    DateInput: DateInput.extend({
      defaultProps: {
        clearable: true,
        firstDayOfWeek: 1,
        valueFormat: DATE_PATTERNS.BR.DATE,
        highlightToday: true,
        locale: "pt-BR",
        defaultLevel: "decade",
        variant: "filled",
        size: "md"
      }
    }),
    ColorInput: ColorInput.extend({
      defaultProps: {
        format: "hex",
        swatches: [
          "#4D4D4D",
          "#999999",
          "#FFFFFF",
          "#F44E3B",
          "#FE9200",
          "#FCDC00",
          "#DBDF00",
          "#A4DD00",
          "#68CCCA",
          "#73D8FF",
          "#AEA1FF",
          "#FDA1FF",
          "#333333",
          "#808080",
          "#cccccc",
          "#D33115",
          "#E27300",
          "#FCC400",
          "#B0BC00",
          "#68BC00",
          "#16A5A5",
          "#009CE0",
          "#7B64FF",
          "#FA28FF",
          "#000000",
          "#666666",
          "#B3B3B3",
          "#9F0500",
          "#C45100",
          "#FB9E00",
          "#808900",
          "#194D33",
          "#0C797D",
          "#0062B1",
          "#653294",
          "#AB149E"
        ],
        swatchesPerRow: 12,
        variant: DEFAULT_VARIANT,
        size: DEFAULT_SIZE
      },
      classNames: {
        label: inputClasses.label,
        error: DEFAULT_ERROR_CLASS_NAMES
      }
    }),
    TextInput: TextInput.extend({
      defaultProps: {
        variant: DEFAULT_VARIANT,
        size: DEFAULT_SIZE
      },
      classNames: {
        label: inputClasses.label,
        error: DEFAULT_ERROR_CLASS_NAMES
      }
    }),
    PasswordInput: PasswordInput.extend({
      defaultProps: {
        variant: DEFAULT_VARIANT,
        size: DEFAULT_SIZE
      },
      classNames: {
        label: inputClasses.label,
        error: DEFAULT_ERROR_CLASS_NAMES
      }
    }),
    NumberInput: NumberInput.extend({
      defaultProps: {
        variant: DEFAULT_VARIANT,
        size: DEFAULT_SIZE
      },
      classNames: {
        label: inputClasses.label,
        error: DEFAULT_ERROR_CLASS_NAMES
      }
    }),
    PinInput: PinInput.extend({
      defaultProps: {
        size: DEFAULT_SIZE,
        inputType: "tel"
      }
    }),
    Select: Select.extend({
      defaultProps: {
        variant: DEFAULT_VARIANT,
        size: DEFAULT_SIZE,
        searchable: true,
        nothingFoundMessage: "Nenhum resultado encontrado",
        clearable: true,
        comboboxProps: { transitionProps: { transition: "pop", duration: 300 } }
      },
      classNames: {
        label: inputClasses.label,
        error: DEFAULT_ERROR_CLASS_NAMES
      }
    }),
    SegmentedControl: SegmentedControl.extend({
      defaultProps: {
        withItemsBorders: false
      }
    }),
    Checkbox: Checkbox.extend({
      defaultProps: {
        radius: "sm"
      }
    }),
    RadioCard: Radio.Card.extend({
      classNames: {
        card: cn(classes.root, "p-(--mantine-spacing-xs)! md:p-(--mantine-spacing-md)! dark:hover:bg-gray-50!")
      }
    }),
    RadioGroup: Radio.Group.extend({
      defaultProps: {
        inputWrapperOrder: ["label", "description", "error", "input"]
      }
    }),
    Modal: Modal.extend({
      defaultProps: {
        overlayProps: {
          backgroundOpacity: 0.55,
          blur: 3
        },
        transitionProps: {
          transition: "fade",
          duration: 300
        },
        scrollAreaComponent: ScrollArea.Autosize
      },
      vars: (theme, props) => {
        if (props.size === "xxl") {
          return {
            root: {
              "--modal-size": "80rem"
            }
          };
        }

        return { root: {} };
      }
    }),
    LoadingOverlay: LoadingOverlay.extend({
      defaultProps: {
        loaderProps: {
          size: "lg",
          color: "primary",
          type: "dots",
          loaders: { ...Loader.defaultLoaders, ring: CustomLoader.Ring, nextCargo: CustomLoader.NextCargo }
        },
        overlayProps: {
          radius: "sm",
          blur: 2
        }
      }
    }),
    Loader: Loader.extend({
      defaultProps: {
        loaders: { ...Loader.defaultLoaders, ring: CustomLoader.Ring, nextCargo: CustomLoader.NextCargo }
      }
    }),
    AppShellMain: AppShellMain.extend({
      defaultProps: {
        mih: "auto"
      }
    }),
    Container: Container.extend({
      vars: (theme, props) => {
        if (props.size === "2xl") {
          return {
            root: {
              "--container-size": "90rem"
            }
          };
        }

        return { root: {} };
      }
    }),
    Button: Button.extend({
      defaultProps: {
        size: DEFAULT_SIZE
      }
    }),
    Fieldset: Fieldset.extend({
      styles(theme, props) {
        return {
          legend: {
            fontSize: theme.fontSizes.xl,
            fontWeight: 700,
            color: props.color ?? theme.colors.gray[7]
          },
          root: {
            borderColor: props.color ?? theme.colors.primary[6]
          }
        };
      }
    }),
    Notification: Notification.extend({
      defaultProps: {
        withBorder: true
      }
    }),
    Portal: Portal.extend({
      defaultProps: {
        target: "#root"
      }
    }),
    Accordion: Accordion.extend({
      defaultProps: {
        variant: "contained"
      }
    }),
    Dialog: Dialog.extend({
      defaultProps: {
        withBorder: true,
        withCloseButton: true
      },
      classNames(theme, props) {
        const colorIsRed = props.color === "red";

        return {
          root: cn({ "bg-red-100!": colorIsRed }),
          closeButton: cn({ "text-red-600! hover:bg-transparent!": colorIsRed })
        };
      }
    }),
    Tooltip: Tooltip.extend({
      defaultProps: {
        transitionProps: { transition: "pop", duration: 300 },
        withArrow: true,
        position: "top",
        color: "gray",
        radius: "sm"
      }
    }),
    Title: Title.extend({
      classNames: {
        root: cn("mb-1!")
      }
    })
  }
});

// export const THEME = mergeMantineTheme(DEFAULT_THEME, themeOverride);
